import React, { useRef, useState, useContext, useEffect } from "react";
import {Form, Row, Modal} from "react-bootstrap";
import _ from "lodash";
import RequestUtils from "../../utils/RequestUtils";
import {RequestContext} from "../../context/RequestContext";
import {
  renderScreenerTypeFields,
  renderProcessGroupFields,
  renderAlternateFormFields,
  renderAdvancedOptions,
  renderSubtitlesLanguages,
  renderAudioSideCarLanguages
} from "../form/FormRenderer";
import {AlertMessage} from "../../constants/AlertMessage";
import moment from "moment";
import ConfigUtils from "../../utils/ConfigUtils";

import "../../styles/form.scss";
import { StyledBlackButton, StyledBlueButton } from "../../styles/BootstrapStyles";

export const parseAudioLayout = (audioLayout) => {
  if (_.isArray(audioLayout)) {
    let data = {};
    _.map(audioLayout, (audio) => {
      if (_.has(audio, "order")) {
        let order = _.get(audio, "order");
        let languageKey = "languageType" + order;
        let configKey = "channelConfig" + order;
        data[languageKey] = _.get(audio, "languageType");
        data[configKey] = _.get(audio, "channelConfig");
      } else {
        data["channelCount"] = _.get(audio, "channelCount");
      }
    });
    return data;
  } else {
    return audioLayout;
  }
};

const WECO = "WeCO";
const WECO_ERROR = "More than one processes cannot exist with eco file type";

const ProcessForm = () => {
  const inputComponents = ConfigUtils.getInputComponents();
  const {
    sourceLocation,
    jobType,
    showProcessForm,
    _setShowProcessForm,
    dueDate,
    sources,
    addNewProcess,
    selectedProcess,
    _setSelectedProcess,
    processes,
    _setEcoProcessError,
    _setDeliveriesToSourceMapping,
    setDcpDateConfirmed
  } = useContext(RequestContext);
  const duplicateRef = useRef(null);
  const [process, setProcess] = useState({});
  const [okWithWaterMark, setOkWithWaterMark] = useState(false);
  const [okayWithWaterMark, setokayWithWaterMark] = useState(false);
  const [additionalFileFields, setAdditionalFileFields] = useState({});
  const [selectedScreenerType, setSelectedScreenerType] = useState("");
  const [screenerTypes, setScreenerTypes] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [fileFields, setFileFields] = useState([]);
  const [error, setError] = useState({});

  const updateFileCreationField = (fileType, fileField = []) => {
    let additionalVal = {};
    let preAir = false;
    const selectedProcessValue = _.find(_.isEmpty(fileField) ? fileFields : fileField, (processValue) => {
      return _.get(processValue, "ref") === fileType;
    });
    const additionDataFields = _.get(selectedProcessValue, "metadata");
    //Adding audio layout
    const defaultAudioMsg = _.get(selectedProcessValue, "defaultAudioMsg", null);
    const defaultAudioLayoutValues = _.get(selectedProcessValue, "defaultAudioLayout", null);
    additionalVal["defaultAudioMessage"] = defaultAudioMsg;
    additionalVal["defaultAudioLayout"] = defaultAudioLayoutValues;

    let updatedFields = _.map(_.cloneDeep(additionDataFields), (meta) => {
      const field = _.get(inputComponents, "inputComponents")[0].dataFields[0][meta];
      if (!_.isEmpty(field)) {
        field.ref = meta;
        return Object.assign({}, field);
      }
    });
    _.map(sources, (source) => {
      if (_.get(source, "preAir", false)) {
        preAir = true;
      }
    });
    if (preAir && _.includes(fileType, "ProRes") && _.isEmpty(fileField)) {
      setOkWithWaterMark(true);
    }
    additionalVal["description"] = _.toLower(fileType) === "custom" ? null : _.get(selectedProcessValue, "description", null);
    additionalVal["advanceField"] =  (fileType === 'Audio WAV' || fileType === 'Work File eCO'
        || fileType === 'Mezzanine ProRes (US Domestic)' || fileType === 'Mezzanine ProRes (International)'
        || (_.get(sourceLocation, "type") === "MORPH_ENCODING") || fileType === "") ? false : true;
    additionalVal["producerPortal"] = fileType === 'Mezzanine ProRes (US Domestic)'
      || fileType === 'Mezzanine ProRes (International)';
    additionalVal["additionalFields"] = RequestUtils.filterProcessorFields(sourceLocation, fileType, updatedFields);
    // Update notes field to be an input. Making this change in json will require lots of changes, so addding this logic here 
    _.forEach(_.get(additionalVal, "additionalFields"), field => {
      if (field.ref === "notes") {
        field.type = "text";
        field.multiLine = false;
      }
    });
    setAdditionalFileFields(additionalVal);
  };

  useEffect(() => {
      if (!_.isEmpty(_.get(jobType, "processing"))) {
        setScreenerTypes(RequestUtils.getProcessMeta(jobType, sourceLocation));
      } else {
        setMetadata(RequestUtils.getProcessFieldsFileCreation(jobType, sourceLocation));
      }
      if (selectedProcess) {
        setProcess(selectedProcess);
        setSelectedScreenerType(_.get(selectedProcess, "screenerType", ""));
        _setSelectedProcess({});
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!_.isEmpty(process)) {
      if (!_.isEmpty(screenerTypes)) {
        let field = RequestUtils.getProcessMetaField(_.get(process, "screenerType"));
        setMetadata(RequestUtils.getMetaDataField1(_.get(field, "metadata"), field));
      } else {
        let fileField = RequestUtils.getFileGroup(metadata, _.get(process, "fileType"));
        setFileFields(fileField);
        updateFileCreationField(_.get(process && _.get(process, "profile"), "description", ""), fileField);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process]);


  const removePortalLanguages = (process) => {
    let processValue = [];
    if (_.get(additionalFileFields, 'producerPortal')) {
      if(!_.get(process,'captions')) {
        processValue.push("captionLanguages");
      }
      if(!_.get(process,'audioSidecar')) {
        processValue.push("audioLanguages");
      }
      return _.omit(process, processValue);
    }
    return process;
  }
  
  const handleCheckPortal = (e) => {
    const currentProcessValue = removePortalLanguages(process);
    setProcess({ ...currentProcessValue, [e.target.name]: e.target.checked });
  }

  const mapNonScreenerFields = () => {
    if (!_.has(process, 'advancedOptions') && _.get(additionalFileFields, 'advanceField')) {
      process["advancedOptions"] = false;
    }
    if (!_.has(process, 'addBurnInTC') && (_.get(additionalFileFields, 'additionalFields')).some(field => (field && field.ref) === "addBurnInTC")) {
      process["addBurnInTC"] = false;
    }
    if (okayWithWaterMark) {
      process["okayWithWaterMark"] = true;
    } else {
      delete process["okayWithWaterMark"];
    }
    if (_.get(additionalFileFields, 'producerPortal')) {
      if (!_.has(process, 'captions')) {
        process["captions"] = false;
      }
      if (!_.has(process, 'audioSidecar')) {
        process["audioSidecar"] = false
      }
    }
    let newProcessVal = removeAdvanceF(process, _.get(process, 'advancedOptions', false));
    newProcessVal = removePortalLanguages(newProcessVal);
    return Object.assign({}, newProcessVal);
  };

  const removeAdvanceF = (processValue, markedValue) => {
    let advanceVal = markedValue || false;
    let currentProcessValue = { ...processValue };
    if (!advanceVal) {
      currentProcessValue = _.omit(currentProcessValue, ["frameRate", "audioLayout"]);
    }
    return currentProcessValue
  }

  const mapScreenerFields = (formData) => {
    let fieldVar = { ...process };
    let errorValue = {};
    let values = {};
    let encryptVal = {};

    _.map(metadata, (field) => {
      //Need to improve this submit
      if (field.ref === "screenerEncryptionType") {
        encryptVal = _.filter(field.reveal, (val) => {
          return _.get(val, "refData") === _.get(fieldVar, field.ref);
        });
      }
      if ((field.type === "number" || field.type === "list") && !_.get(field, "isEditable", false)) {
        values[field.ref] = _.get(fieldVar, field.ref, formData[field.ref] && formData[field.ref].value);
      }
      if (_.get(formData[field.ref], "type") === "checkbox") {
        values[field.ref] = formData[field.ref].checked;
      }
      if (_.get(field, "type") === "radio") {
        values[field.ref] = _.get(fieldVar, field.ref, "");
        if (_.isEmpty(values[field.ref]) && field.required) {
          errorValue = { ...error, [field.ref]: "Please fill out this field" };
        }
      }
    });
    if (!_.isEmpty(encryptVal) && _.get(process, "screenerType") === "DCP") {
      let getDate = handleDateCheck(encryptVal);
      fieldVar = Object.assign(fieldVar, _.get(getDate, "value"));
      errorValue = Object.assign(errorValue, _.get(getDate, "error"));
    }
    if (!_.isEmpty(errorValue)) {
      setError(errorValue);
      return null;
    } else {
      return Object.assign(values, fieldVar);
    }
  }

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (processIsEco() && processes.length > 0 && !(processes.length === 1 && _.get(processes[0], "key") === _.get(process, "key"))) {
      _setEcoProcessError(WECO_ERROR);
      _setShowProcessForm(false);
      return;
    }
    let mappedProcess = _.isEmpty(screenerTypes) ? mapNonScreenerFields() : mapScreenerFields(e.target);
    if (_.isNil(mappedProcess)) {
      return;
    }
    addNewProcess(mappedProcess);
    // Remove delivery to source mapping if there is a change in screener type
    if (_.get(jobType, "jobType") === "EVENT_SCREENERS" && selectedScreenerType !== _.get(process, "screenerType", "")) {
      _setDeliveriesToSourceMapping({});
    }
    _setShowProcessForm(false);
  };

  const processIsEco = () => {
    let profile = _.get(process, "profile");
    if (profile) {
      if (_.get(profile, "code") === WECO) {
        return true;
      }
    }
    return false;
  }

  const handleSaveAndDuplicate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (processIsEco() && processes.length > 0) {
      _setEcoProcessError(WECO_ERROR);
      _setShowProcessForm(false);
      return;
    }
    let formData = duplicateRef.current;
    if (!formData.checkValidity()) {
      formData.reportValidity();
      return;
    }
    let processValNew = removeAdvanceF(process, _.get(process, 'advancedOptions', false));
    processValNew = removePortalLanguages(processValNew);
    let value = _.isEmpty(screenerTypes)
      ? Object.assign({}, { ...processValNew })
      : mapScreenerFields(formData);
    if (!_.isNil(value)) {
      addNewProcess(value);
      setProcess(_.omit(value, "key"));
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    _setShowProcessForm(false);
  };

  const handleClose = () => {
    _setShowProcessForm(false);
  };

  const handleScreenerTypeFieldChange = (e) => {
    e.preventDefault();
    let key = _.get(process, "key");
    let newProcess = Object.assign({}, { screenerType: e.target.value });
    if (!_.isNil(key)) {
      newProcess["key"] = key;
    }
    setProcess(newProcess);
    let field = RequestUtils.getProcessMetaField(e.target.value);
    setMetadata(RequestUtils.getMetaDataField1(_.get(field, "metadata"), field));
  }

  const handleProcessGroupFieldChange = (e) => {
    e.preventDefault();
    let key = _.get(process, "key");
    let value = _.pick(metadata, "key") || {};
    let newProcess = Object.assign(value, { fileType: e.target.value });
    if (!_.isNil(key)) {
      newProcess["key"] = key;
    }
    setProcess(newProcess);
    setFileFields(RequestUtils.getFileGroup(metadata, e.target.value));
  }

  const handleFileTypeChange = (e) => {
    e.preventDefault();
    let fileValues = _.pick(process, ["key", "fileType"]);
    fileValues["profile"] = {
      description: e.target.value
    };
    const getAbbr = _.find(fileFields, (processValue) => {
      return _.get(processValue, "ref") === e.target.value;
    });
    if (!_.isEmpty(_.get(getAbbr, "abbr", ""))) {
      fileValues["profile"].code = _.get(getAbbr, "abbr", "");
    }
    setProcess(fileValues);
    updateFileCreationField(e.target.value);
  }

  const handleFileSelect = (fileType) => {
    let processVal = _.pick(process, "key") || {};
    const getProcess = RequestUtils.getFileField(fileType, _.get(sourceLocation, "type"));
    let abbr = _.get(getProcess, "abbr", "");
    processVal["fileType"] = _.get(getProcess, "group");
    processVal["profile"] = {
      description: fileType
    };
    if (!_.isEmpty(abbr)) {
      processVal["profile"].code = abbr;
    }
    setProcess(Object.assign({}, processVal));
    let fileField = RequestUtils.getFileGroup(metadata, _.get(getProcess, "group"));
    setFileFields(fileField);
    updateFileCreationField(fileType, fileField);
  };

  const timeError = {
    date: "The End Time must be greater than the Start Time if Start and End Dates are on the same day.",
  };
  const dateError = {
    date: "The End Date must be greater than the Start Date."
  };
  const startDateError = {
    date: "The Start Date must not be before the Due Date of this request."
  };
  const END_DATE_MATCH = /EndDate/i;
  const START_DATE_MATCH = /StartDate/i;
  const START_TIME_MATCH = /StartTime/i;
  const DATE_MATCH = /Date/i;
  const DUE_DATE_DEFAULT = "00:00";

  const handleDateCheck = (encryptVal) => {
    let componentF = _.get(encryptVal[0], "components", []);
    let timeField = _.filter(componentF, (val) => {
      return val.match(/Time/i);
    });
    let dateField = _.filter(componentF, (val) => {
      return val.match(/Date/i);
    });
    let errorVal = {};
    let findEncrypt = {};
    let startDate, endDate, startTime, endTime;

    _.map(_.union(dateField, timeField), (field) => {
      let dfault = field.match(DATE_MATCH) ? dueDate : DUE_DATE_DEFAULT;
      if (field.match(START_DATE_MATCH)) {
        startDate = _.get(process, field, dfault);
        errorVal = moment(startDate).isBefore(moment(dueDate)) ? startDateError : {};
      } else if (field.match(END_DATE_MATCH)) {
        endDate = _.get(process, field, dfault);
      } else if (field.match(START_TIME_MATCH)) {
        startTime = _.get(process, field, dfault);
      } else {
        endTime = _.get(process, field, dfault);
      }
      findEncrypt = { ...findEncrypt, [field]: _.get(process, field, dfault) };
    });

    if (startDate === endDate && startTime >= endTime) {
      return { error: timeError, value: findEncrypt };
    }
    if (startDate > endDate) {
      return { error: dateError, value: findEncrypt };
    }
    return { error: errorVal, value: findEncrypt };
  };

  const handleOption = (field, value) => {
    if(field === "KdmStartDate" || field === "KdmEndDate" || field === "KdmStartTime" || field === "KdmEndTime" || 
       field === "DKdmStartDate" || field === "DKdmEndDate" || field === "DKdmStartTime" || field === "DKdmEndTime"){
      if(process[field] !== value){
        setDcpDateConfirmed(true);
      }else{
        setDcpDateConfirmed(false);
      }
    }
    setProcess({ ...process, [field]: value });
  };

  const handleMultiple = (field, value, updatedData) => {
    setProcess({ ...updatedData, [field]: value });
  };

  const handleAdvancedOption = (e) => {
    e.preventDefault();
    setProcess({ ...process, frameRate: e.target.value });
  }

  const handleLanguages = (e) => {
    e.preventDefault();
    setProcess({ ...process, [e.target.name]: e.target.value });
  }

  const updateAudioLayout = (channel, customAudioLayout) => {
    if (_.get(process, "advancedOptions")) {
      let data = {};
      if (parseInt(channel) > 0) {
        let data1 = [];
        _.times(channel, (count) => {
          let test = {};
          let languageKey = "languageType" + (count + 1);
          let configKey = "channelConfig" + (count + 1);
          test["order"] = count + 1;
          test["languageType"] = (document.getElementById(languageKey) as HTMLInputElement).innerText;
          test["channelConfig"] = (document.getElementById(configKey) as HTMLInputElement).innerText;
          data1.push(Object.assign(test, customAudioLayout));
        });
        data = data1;
      } else {
        let dataRef = {};
        dataRef["channelCount"] = !_.isEmpty(_.get(additionalFileFields, "defaultAudioLayout")) ? "Default Audio Layout" : "Same as source";
        if (!_.isEmpty(_.get(additionalFileFields, "defaultAudioLayout"))) {
          dataRef["defaultAudioLayout"] = true;
        }
        data = [dataRef];
      }
      setProcess({ ...process, audioLayout: data });
    } else {
      delete process["audioLayout"];
    }
  }
  return (
      <Modal
          className="modal process-form-modal"
          backdrop="static"
          show={showProcessForm}
          centered
          size="lg"
          dialogClassName="modalDialog"
          onHide={handleClose}>
        <Modal.Header className="modalHeader" closeButton>
          <Modal.Title className="modalHeaderTitle">Add/Edit Process</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBody">
          <Form
              ref={duplicateRef}
              onSubmit={handleSave}>
            {!_.isEmpty(screenerTypes) ?
                renderScreenerTypeFields(
                    process,
                    screenerTypes,
                    handleScreenerTypeFieldChange) :
                renderProcessGroupFields(
                    sourceLocation,
                    process,
                    metadata,
                    fileFields,
                    handleProcessGroupFieldChange,
                    handleFileTypeChange,
                    handleFileSelect
                )
            }
            {!_.isEmpty(_.get(additionalFileFields, "description")) && !_.get(process, "advancedOptions", false) ? (
                <Form.Group
                    className="m-0 p-0"
                    controlId="description"
                    as={Row}>
                  <Form.Label className="formLabel">File Type Description</Form.Label>
                  <pre className="fileDescription" dangerouslySetInnerHTML={{__html: _.get(additionalFileFields, "description", null)}} />
                </Form.Group>
            ) : null}
            {_.isEmpty(_.get(process, "screenerType"))
                ? null
                : renderAlternateFormFields(
                    metadata,
                    true,
                    handleOption,
                    process,
                    error,
                    dueDate,
                    handleMultiple)}
            {_.get(additionalFileFields, "advanceField", false) ? (
                <Form.Group
                    key={"group-advance-options"}
                    className="m-0 p-0" as={Row}
                    controlId="advancedOptions">
                    <Form.Check
                        type="checkbox"
                        className="formCheckbox"
                        name="advance_checkbox"
                        required={false}
                        checked={_.get(process, "advancedOptions", false)}
                        onChange={(e) => {
                          let currentProcessValue = removeAdvanceF(process, e.target.checked)
                          setProcess({ ...currentProcessValue, advancedOptions: e.target.checked });
                        }}
                        value={_.get(process, "advancedOptions", false)}
                        label={"Advance Options?"}/>
                </Form.Group>
            ) : null}
            {_.get(additionalFileFields, "producerPortal") ? (
            <>
              <Form.Group
                key={"group-captions"}
                className="m-0 p-0" as={Row}
                controlId="captions">
                <Form.Check
                  type="checkbox"
                  className="formCheckbox"
                  name="captions"
                  checked={_.get(process, "captions", false)}
                  onChange={handleCheckPortal}
                  value={_.get(process, "captions", false)}
                  label={"Include Captions/Subtitles"} />
              </Form.Group>
              {_.get(additionalFileFields, "producerPortal") && _.get(process, "captions")
                ? renderSubtitlesLanguages(
                  process,
                  handleLanguages
                ) : null}
            </>
            ) : null}
            {_.get(additionalFileFields, "producerPortal") ? (
            <>
              <Form.Group
                key={"group-audio-sidecar"}
                className="m-0 p-0" as={Row}
                controlId="audioSidecar">
                <Form.Check
                  type="checkbox"
                  className="formCheckbox"
                  name="audioSidecar"
                  checked={_.get(process, "audioSidecar", false)}
                  onChange={handleCheckPortal}
                  value={_.get(process, "audioSidecar", false)}
                  label={"Include Audio Sidecar"} />
              </Form.Group>
              {_.get(additionalFileFields, "producerPortal") && _.get(process, "audioSidecar")
                ? renderAudioSideCarLanguages(
                  process,
                  handleLanguages
                ) : null}
            </>
            ) : null}
            {!_.isEmpty(_.get(additionalFileFields, "additionalFields")) && _.isEmpty(screenerTypes)
                ? renderAlternateFormFields(
                    _.get(additionalFileFields, "additionalFields"),
                    true,
                    handleOption,
                    process,
                    error,
                    dueDate,
                    handleMultiple
                )
                : null}
            {_.get(additionalFileFields, "advanceField", false)
                ? renderAdvancedOptions(
                    process,
                    additionalFileFields,
                    handleAdvancedOption,
                    updateAudioLayout
                ) : null}
            <Form.Row
                style={{
                  padding: "15px 15px 0 15px",
                  justifyContent: "right"
                }}>
              <StyledBlueButton
                  id={"save-duplicate-process"}
                  onClick={handleSaveAndDuplicate}>
                Save &amp; Duplicate
              </StyledBlueButton>
              <StyledBlueButton
                  id={"save-process"}
                  type="submit">
                Save
              </StyledBlueButton>
              <StyledBlackButton
                  id={"cancel-process"}
                  onClick={handleCancel
                  }>
                Cancel
              </StyledBlackButton>
            </Form.Row>
          </Form>
        </Modal.Body>
        {!_.isEmpty(_.get(error, "date")) || okWithWaterMark ? (
            <Modal className="modal" show={true}
                   dialogClassName="modalDialog">
              <Modal.Header className="modalHeader">
                <Modal.Title className="modalHeaderTitle">{okWithWaterMark ? "Message" : "Action Required"}</Modal.Title>
              </Modal.Header>
              <Modal.Body className="modalBody">
                <p>{okWithWaterMark ? AlertMessage.OkWithWaterMark : _.get(error, "date")}</p>
              </Modal.Body>
              <Modal.Footer>
                <StyledBlueButton
                    onClick={() => {
                      if (okWithWaterMark) {
                        setOkWithWaterMark(false);
                        setokayWithWaterMark(true);
                      } else {
                        setError(_.omit(error, "date"));
                      }
                    }}
                    variant="secondary">
                  OK
                </StyledBlueButton>
              </Modal.Footer>
            </Modal>
        ) : null}
      </Modal>
  );
};

export default ProcessForm;
